"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import {
  GA4_MEASUREMENT_ID,
  hasTrackingConsent,
  updateGAConsent,
} from "@/lib/analytics";

export default function GoogleAnalyticsProvider({ children }) {
  const [consentGranted, setConsentGranted] = useState(false);

  // Check consent on mount and when it changes
  useEffect(() => {
    const checkConsent = () => {
      const hasConsent = hasTrackingConsent();
      setConsentGranted(hasConsent);

      updateGAConsent(hasConsent ? "granted" : "denied");

      if (!hasConsent) {
        window.GA_INITIALIZED = false;
        return;
      }

      updateGAConsent("granted");
    };

    // Check immediately
    checkConsent();

    // Listen for consent changes via custom event instead of polling
    const handleConsentChange = () => {
      checkConsent();
    };

    window.addEventListener('consentChanged', handleConsentChange);

    // Cleanup
    return () => {
      window.removeEventListener('consentChanged', handleConsentChange);
    };
  }, []);

  // Only render GA scripts if consent is granted and we have a GA ID
  if (!consentGranted || !GA4_MEASUREMENT_ID) {
    return <>{children}</>;
  }

  return (
    <>
      <Script
        id="ga4-loader"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`}
      />
      <Script
        id="ga4-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            window.gtag = window.gtag || function(){window.dataLayer.push(arguments);};
            window.gtag('js', new Date());
            window.gtag('consent', 'default', {
              ad_storage: 'denied',
              ad_user_data: 'denied',
              ad_personalization: 'denied',
              analytics_storage: 'granted',
              functionality_storage: 'granted',
              security_storage: 'granted'
            });
            window.gtag('config', '${GA4_MEASUREMENT_ID}', {
              send_page_view: false,
              anonymize_ip: true,
              debug_mode: ${process.env.NODE_ENV === "development"},
            });
            window.GA_INITIALIZED = true;
            window.dispatchEvent(new Event('ga4-ready'));
          `,
        }}
      />
      {children}
    </>
  );
}
